/**
 * 📝 FORMULÁRIO DE CRIAÇÃO/EDIÇÃO DE KAIZEN
 * Interface para registrar melhorias e configurar métricas
 */

import React, { useState, useEffect } from 'react';
import { X, Save, TrendingDown, DollarSign, Users } from 'lucide-react';
import GainsMonitorService from '../services/gainsMonitorService';
import type { KaizenRecord, KaizenType, KaizenStatus } from '../types/gainsMonitor';
import type { KaizenOriginDraft } from '../utils/problemKaizenLink';
import { metricValidationError, parseMetricInput, type KaizenMetricKind } from '../utils/kaizenMetricSemantics';

function storedMetric(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

interface KaizenFormProps {
  kaizen?: KaizenRecord | null;
  projectId?: string | null;
  organizationId: string;
  origin?: KaizenOriginDraft | null;
  defaultTitle?: string;
  defaultDescription?: string;
  onSuccess: (created?: KaizenRecord | null) => void;
  onCancel: () => void;
}

const KAIZEN_TYPES: { value: KaizenType; label: string }[] = [
  { value: 'reducao_lead_time', label: 'Redução de Lead Time' },
  { value: 'eliminacao_desperdicio', label: 'Eliminação de Desperdício' },
  { value: 'automacao', label: 'Automação' },
  { value: 'padronizacao', label: 'Padronização' },
  { value: 'qualidade', label: 'Qualidade' },
  { value: 'ergonomia', label: 'Ergonomia' }
];

const KAIZEN_STATUSES: { value: KaizenStatus; label: string }[] = [
  { value: 'planejado', label: 'Planejado' },
  { value: 'em_implementacao', label: 'Em Implementação' },
  { value: 'implementado', label: 'Implementado' },
  { value: 'em_monitoramento', label: 'Em Monitoramento' },
  { value: 'sustentado', label: 'Sustentado' },
  { value: 'regredido', label: 'Regredido' }
];

export const KaizenForm: React.FC<KaizenFormProps> = ({
  kaizen,
  projectId,
  organizationId,
  origin,
  defaultTitle,
  defaultDescription,
  onSuccess,
  onCancel
}) => {
  
  const [formData, setFormData] = useState({
    title: kaizen ? (kaizen.title || '') : origin ? (origin.title || '') : (defaultTitle || ''),
    description: kaizen ? (kaizen.description || '') : origin ? (origin.description || '') : (defaultDescription || ''),
    type: kaizen?.type || 'reducao_lead_time' as KaizenType,
    responsible: kaizen?.responsible || '',
    team_members: kaizen?.team_members?.join(', ') || '',
    
    planned_date: kaizen?.planned_date 
      ? new Date(kaizen.planned_date).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    implementation_date: kaizen?.implementation_date
      ? new Date(kaizen.implementation_date).toISOString().split('T')[0]
      : '',
    target_date: kaizen?.target_date
      ? new Date(kaizen.target_date).toISOString().split('T')[0]
      : new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    
    baseline_lead_time: storedMetric(kaizen?.baseline_lead_time),
    baseline_cycle_time: storedMetric(kaizen?.baseline_cycle_time),
    baseline_ftr: storedMetric(kaizen?.baseline_ftr),
    baseline_capacity_fte: storedMetric(kaizen?.baseline_capacity_fte),
    baseline_cost: storedMetric(kaizen?.baseline_cost),
    
    target_lead_time: storedMetric(kaizen?.target_lead_time),
    target_cycle_time: storedMetric(kaizen?.target_cycle_time),
    target_ftr: storedMetric(kaizen?.target_ftr),
    target_capacity_fte: storedMetric(kaizen?.target_capacity_fte),
    target_cost: storedMetric(kaizen?.target_cost),
    
    status: kaizen?.status || 'planejado' as KaizenStatus,
    confidence_level: kaizen?.confidence_level || 'media' as 'baixa' | 'media' | 'alta'
  });

  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Calcular reduções planejadas
  const plannedReduction = {
    lead_time: formData.baseline_lead_time != null && formData.target_lead_time != null && formData.baseline_lead_time > 0
      ? ((formData.baseline_lead_time - formData.target_lead_time) / formData.baseline_lead_time) * 100
      : 0,
    cost: formData.baseline_cost != null && formData.target_cost != null && formData.baseline_cost > 0
      ? ((formData.baseline_cost - formData.target_cost) / formData.baseline_cost) * 100
      : 0,
    fte: formData.baseline_capacity_fte != null && formData.target_capacity_fte != null
      ? formData.baseline_capacity_fte - formData.target_capacity_fte
      : 0,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const metricChecks: Array<[KaizenMetricKind, number | null]> = [
        ['duration', formData.baseline_lead_time],
        ['duration', formData.target_lead_time],
        ['duration', formData.baseline_cycle_time],
        ['duration', formData.target_cycle_time],
        ['percent', formData.baseline_ftr],
        ['percent', formData.target_ftr],
        ['fte', formData.baseline_capacity_fte],
        ['fte', formData.target_capacity_fte],
        ['money', formData.baseline_cost],
        ['money', formData.target_cost],
      ];
      const invalid = metricChecks
        .map(([kind, value]) => metricValidationError(kind, value))
        .find((message) => message != null);
      if (invalid) {
        alert(invalid);
        setSaving(false);
        return;
      }

      const linkedProjectId = projectId || origin?.project_id || undefined;
      const data = {
        ...formData,
        project_id: linkedProjectId,
        workflow_id: origin?.workflow_id ?? undefined,
        organization_id: organizationId,
        team_members: formData.team_members
          .split(',')
          .map(m => m.trim())
          .filter(Boolean),
        planned_date: new Date(formData.planned_date),
        implementation_date: formData.implementation_date 
          ? new Date(formData.implementation_date)
          : undefined,
        target_date: new Date(formData.target_date)
      };

      if (kaizen) {
        await GainsMonitorService.updateKaizen(kaizen.id, data);
        onSuccess(kaizen);
      } else {
        const created = await GainsMonitorService.createKaizen(data);
        if (!created) throw new Error('Não foi possível criar o Kaizen');
        onSuccess(created);
      }
    } catch (error) {
      console.error('Error saving kaizen:', error);
      alert('Erro ao salvar Kaizen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {kaizen ? 'Editar Kaizen' : 'Novo Kaizen'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {origin ? (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Origem</p>
              <p className="mt-1 font-semibold text-slate-900">{origin.problem_label}</p>
              <p>{origin.problem_title}</p>
              {origin.operational_label ? <p className="mt-1">{origin.operational_label}</p> : null}
              {origin.opening_wip != null ? (
                <p className="mt-1 text-xs text-slate-500">
                  WIP na abertura: {origin.opening_wip}. Esse número não preenche baseline, meta nem capacidade.
                </p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-3 text-xs font-semibold">
                <a className="text-blue-700 hover:underline" href={`/problemas/${origin.problem_id}`}>Ver problema</a>
                {origin.return_path ? (
                  <a className="text-blue-700 hover:underline" href={origin.return_path}>Ver contexto operacional</a>
                ) : null}
              </div>
            </div>
          ) : null}

          {/* Identificação */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
              1️⃣ Identificação
            </h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Título da Melhoria *
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: Redução de Lead Time - Aprovação de Orçamento"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descreva a melhoria implementada..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Melhoria *
                </label>
                <select
                  required
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value as KaizenType })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {KAIZEN_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status *
                </label>
                <select
                  required
                  value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value as KaizenStatus })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {KAIZEN_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsável *
                </label>
                <input
                  type="text"
                  required
                  value={formData.responsible}
                  onChange={e => setFormData({ ...formData, responsible: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome do responsável"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Time (separado por vírgula)
                </label>
                <input
                  type="text"
                  value={formData.team_members}
                  onChange={e => setFormData({ ...formData, team_members: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Maria, João, Pedro"
                />
              </div>
            </div>
          </div>

          {/* Datas */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
              2️⃣ Cronograma
            </h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Planejamento
                </label>
                <input
                  type="date"
                  value={formData.planned_date}
                  onChange={e => setFormData({ ...formData, planned_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data de Implementação
                </label>
                <input
                  type="date"
                  value={formData.implementation_date}
                  onChange={e => setFormData({ ...formData, implementation_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data Alvo (Target)
                </label>
                <input
                  type="date"
                  value={formData.target_date}
                  onChange={e => setFormData({ ...formData, target_date: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Métricas Lead Time */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2 flex items-center gap-2">
              3️⃣ Métricas de Lead Time
              <TrendingDown className="w-5 h-5 text-blue-600" />
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase">Baseline (Antes)</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Time (minutos)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="min"
                    value={formData.baseline_lead_time ?? ''}
                    onChange={e => setFormData({ ...formData, baseline_lead_time: parseMetricInput(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    FTR - First Time Right (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="%"
                    value={formData.baseline_ftr ?? ''}
                    onChange={e => setFormData({ ...formData, baseline_ftr: parseMetricInput(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase">Target (Meta)</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Lead Time (minutos)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="min"
                    value={formData.target_lead_time ?? ''}
                    onChange={e => setFormData({ ...formData, target_lead_time: parseMetricInput(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    FTR - First Time Right (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    placeholder="%"
                    value={formData.target_ftr ?? ''}
                    onChange={e => setFormData({ ...formData, target_ftr: parseMetricInput(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Preview da Redução */}
            {plannedReduction.lead_time > 0 && (
              <div className="bg-green-50 border border-green-200 rounded p-4">
                <div className="flex items-center gap-2 text-green-900 font-semibold mb-2">
                  <TrendingDown className="w-5 h-5" />
                  Redução Planejada
                </div>
                <div className="text-2xl font-bold text-green-600">
                  {plannedReduction.lead_time.toFixed(1)}%
                </div>
                <div className="text-sm text-green-700">
                  {formData.baseline_lead_time != null && formData.target_lead_time != null
                    ? `${GainsMonitorService.formatTime(formData.baseline_lead_time - formData.target_lead_time)} economizados`
                    : null}
                </div>
              </div>
            )}
          </div>

          {/* Métricas Avançadas (Colapsável) */}
          <div>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              {showAdvanced ? '▼' : '▶'} Métricas Avançadas (Custo, Capacidade)
            </button>

            {showAdvanced && (
              <div className="mt-4 space-y-4 p-4 bg-gray-50 rounded">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custo Baseline (R$)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="R$"
                        value={formData.baseline_cost ?? ''}
                        onChange={e => setFormData({ ...formData, baseline_cost: parseMetricInput(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacidade Baseline (FTE)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="FTE"
                        value={formData.baseline_capacity_fte ?? ''}
                        onChange={e => setFormData({ ...formData, baseline_capacity_fte: parseMetricInput(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Custo Target (R$)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="R$"
                        value={formData.target_cost ?? ''}
                        onChange={e => setFormData({ ...formData, target_cost: parseMetricInput(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Capacidade Target (FTE)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="FTE"
                        value={formData.target_capacity_fte ?? ''}
                        onChange={e => setFormData({ ...formData, target_capacity_fte: parseMetricInput(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer / Actions */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50 font-medium text-gray-700 transition-colors"
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Salvar Kaizen
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KaizenForm;
